import React, { useState } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  TextField,
  Button,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Link,
  Grid,
  Card,
  CardContent,
  useTheme
} from '@mui/material';
import {
  DeleteForever as DeleteIcon,
  Email as EmailIcon,
  Security as SecurityIcon,
  ArrowForward as ArrowIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { isValidEmail } from '../utils/validationUtils';

const DataDeletionRequest: React.FC = () => {
  const [email, setEmail] = useState('');
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const theme = useTheme();
  const { currentUser } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !isValidEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);
    setError(null);
    
    try {
      // In a real implementation, this would send the request to a backend service
      // For now, we'll simulate a successful submission after a short delay
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Send email to privacy team
      const subject = encodeURIComponent('Data Deletion Request');
      const body = encodeURIComponent(`Email: ${email}\nReason: ${reason || 'Not provided'}\n\nPlease delete all my data associated with this email address from the AmslerGridDaily application.`);
      
      // Open email client with pre-filled email
      window.open(`mailto:amslerGrid_privacy@kubomaster.llc?subject=${subject}&body=${body}`);
      
      // Show success message
      setSubmitted(true);
    } catch (err) {
      setError('Failed to submit request. Please try again or contact us directly.');
      console.error('Error submitting deletion request:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Hero Section */}
      <Box sx={{ 
        textAlign: 'center', 
        mb: 6,
        p: 4,
        borderRadius: 2,
        background: `linear-gradient(45deg, ${theme.palette.error.dark}, ${theme.palette.error.main})`,
        color: 'white'
      }}>
        <Typography variant="h3" component="h1" gutterBottom fontWeight="bold">
          Data Deletion Request
        </Typography>
        <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
          Request the permanent removal of your personal data from AmslerGridDaily
        </Typography>
        
        {currentUser ? (
          <Button
            variant="contained"
            color="secondary"
            size="large"
            component={RouterLink}
            to="/data-management"
            endIcon={<ArrowIcon />}
            sx={{ 
              mt: 2,
              px: 4,
              py: 1.5,
              borderRadius: 2,
              fontWeight: 'bold'
            }}
          >
            Manage Your Data Directly
          </Button>
        ) : (
          <Button
            variant="contained"
            color="secondary"
            size="large"
            component={RouterLink}
            to="/login"
            endIcon={<ArrowIcon />}
            sx={{ 
              mt: 2,
              px: 4,
              py: 1.5,
              borderRadius: 2,
              fontWeight: 'bold'
            }}
          >
            Sign In To Manage Your Data
          </Button>
        )}
      </Box>

      <Grid container spacing={4}>
        <Grid item xs={12} md={7}>
          <Paper elevation={3} sx={{ p: 4 }}>
            {submitted ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CheckCircleIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
                <Typography variant="h5" gutterBottom>
                  Request Submitted
                </Typography>
                <Typography variant="body1" paragraph>
                  Your data deletion request has been submitted. Our privacy team will process your request and contact you at {email} with any additional information.
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Please allow up to 30 days for your request to be processed. If you don't receive a confirmation within 5 business days, please contact us directly.
                </Typography>
                <Button
                  variant="outlined"
                  component={RouterLink}
                  to="/"
                  sx={{ mt: 2 }}
                >
                  Return to Home
                </Button>
              </Box>
            ) : (
              <form onSubmit={handleSubmit}>
                <Typography variant="h5" gutterBottom>
                  Submit a Data Deletion Request
                </Typography>
                
                <Typography variant="body1" paragraph>
                  Fill out this form to request the deletion of your personal data from AmslerGridDaily. Please provide the email address associated with your account.
                </Typography>
                
                {error && (
                  <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                  </Alert>
                )}
                
                <TextField
                  label="Email Address"
                  type="email"
                  fullWidth
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  margin="normal"
                  variant="outlined"
                />
                
                <TextField
                  label="Reason for Deletion (Optional)"
                  multiline
                  rows={3}
                  fullWidth
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  margin="normal"
                  variant="outlined"
                  placeholder="Please let us know why you're requesting data deletion (optional)"
                />
                
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Button
                    component={RouterLink}
                    to="/login"
                    color="primary"
                  >
                    Sign in instead
                  </Button>
                  
                  <Button
                    type="submit"
                    variant="contained"
                    color="error"
                    disabled={isSubmitting}
                    startIcon={isSubmitting ? <CircularProgress size={20} /> : <DeleteIcon />}
                    sx={{ px: 4 }}
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Request'}
                  </Button>
                </Box>
              </form>
            )}
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={5}>
          <Card elevation={2} sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <InfoIcon sx={{ mr: 1, color: theme.palette.info.main }} />
                About Data Deletion
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="body2" paragraph>
                When you request data deletion, we will:
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon fontSize="small" color="success" />
                  </ListItemIcon>
                  <ListItemText primary="Remove all your personal information" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon fontSize="small" color="success" />
                  </ListItemIcon>
                  <ListItemText primary="Delete all your test results and history" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon fontSize="small" color="success" />
                  </ListItemIcon>
                  <ListItemText primary="Erase your account details" />
                </ListItem>
              </List>
              <Typography variant="body2" sx={{ mt: 2 }}>
                This process may take up to 30 days to complete in accordance with applicable privacy laws.
              </Typography>
            </CardContent>
          </Card>
          
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <SecurityIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                Already Have an Account?
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="body2" paragraph>
                If you already have an account, you can manage your data directly:
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <ArrowIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Sign in to your account" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <ArrowIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Go to Profile > Data Management" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <ArrowIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Delete your data or entire account" />
                </ListItem>
              </List>
              <Button
                variant="outlined"
                color="primary"
                fullWidth
                component={RouterLink}
                to="/login"
                startIcon={<EmailIcon />}
                sx={{ mt: 2 }}
              >
                Sign In
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default DataDeletionRequest; 